import {
  IonButton, IonCol, IonGrid, IonInput, IonLabel, IonRow, IonSelect, IonSelectOption, IonText,
} from '@ionic/react';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import ErrorModal from '@/assets/imgs/password-error.svg';
import SuccessModal from '@/components/common/SucessModal';
import { ROUTES_PATH } from '@/constants';
import useModal from '@/hooks/useModal';
import {
  EcommerceDataForm, EcommercePlatform, PluginPlatform, pluginPlatform, PluginPlatforms,
} from '@/interfaces/PluginInterface/PluginInterface';
import { updatePlatform } from '@/redux/ecommerceWidget/ecommerceWidgetAPI';

import styles from '../Plugin.module.scss';
import stylesEcommerce from './EcommerceData.module.scss';

interface EcommerceDataProps {
  platformData: EcommercePlatform | undefined;
  handleWidget?: (changeWidget: boolean, platform_id: string) => void;
  setLoadingState: (loading: boolean) => void;
}

export default function EcommerceData({ platformData, handleWidget, setLoadingState }: EcommerceDataProps) {
  const [ecommmerceFormData, setEcommmerceFormData] = useState<EcommerceDataForm | null>(null);
  const [platformSelected, setPlatformSelected] = useState<string>('');
  const [dataChanged, setDataChanged] = useState<boolean>(false);
  const { presentModal: presentSuccessModal } = useModal(SuccessModal);
  const [errorUrlPro, setErrorUrlPro] = useState<string | null>(null);
  const [errorUrlTest, setErrorUrlTest] = useState<string | null>(null);
  const [errorPlatform, setErrorPlatform] = useState<string | null>(null);

  const validateUrlPro = (url: string) => {
    if (!url || url.trim() === '') {
      setErrorUrlPro('Indica una URL de producción válida');
      return false;
    }
    const urls = url.split(',').map((u: string) => u.trim());
    if (urls.length === 0 || urls.some((u: string) => u === '')) {
      setErrorUrlPro('Revisa las urls, si hay más de una debe separarlas por comas');
      return false;
    }
    setErrorUrlPro(null);
    return true;
  };
  const validateUrlTest = (url: string) => {
    if (!url || url.trim() === '') {
      setErrorUrlTest('Indica una URL de test válida');
      return false;
    }
    const urls = url.split(',').map((u: string) => u.trim());
    if (urls.length === 0 || urls.some((u: string) => u === '')) {
      setErrorUrlTest('Revisa las urls, si hay más de una debe separarlas por comas');
      return false;
    }
    setErrorUrlTest(null);
    return true;
  };
  const validatePlatform = (platform: string) => {
    if (!platform || platform.trim() === '') {
      setErrorPlatform('Selecciona una plataforma');
      return false;
    }
    setErrorPlatform(null);
    return true;
  };

  const history = useHistory();

  const submitEcommerceData = async () => {
    const urlPro: boolean = validateUrlPro(ecommmerceFormData?.url_production || '');
    const urlTest: boolean = validateUrlTest(ecommmerceFormData?.url_test || '');
    const platform: boolean = validatePlatform(platformSelected);
    if (!urlPro || !urlTest || !platform) {
      return;
    }
    // si cart_uri o macrodata_uri son nulos o undefined, se les asigna ""
    if (ecommmerceFormData?.cart_uri === undefined) {
      ecommmerceFormData!.cart_uri = '';
    }
    if (ecommmerceFormData?.macrodata_uri === undefined) {
      ecommmerceFormData!.macrodata_uri = '';
    }
    const productionUrlArray = [];
    const testUrlArray = [];
    // Split the URLs by comma and trim any whitespace
    if (ecommmerceFormData?.url_production) {
      productionUrlArray.push(...ecommmerceFormData.url_production
        .split(',')
        .map((url: string) => url.trim())
        .filter((url: string) => url !== '')); // si es una cadena vacía no se añade
    }
    if (ecommmerceFormData?.url_test) {
      testUrlArray.push(...ecommmerceFormData.url_test.split(',')
        .map((url: string) => url.trim())
        .filter((url: string) => url !== '')); // si es una cadena vacía no se añade
    }
    const updatedData = {
      platform: platformSelected,
      url_production: productionUrlArray,
      url_test: testUrlArray,
      macrodata_uri: `${ecommmerceFormData?.macrodata_uri}${handleMacrodataEndpoint(platformSelected)}`,
      cart_uri: `${ecommmerceFormData?.cart_uri}${handleCartEndpoint(platformSelected)}`,
    } as EcommercePlatform;

    try {
      // setLoadingState(true);
      setDataChanged(false);
      const resoulPlatform = await updatePlatform(updatedData);
      if (handleWidget) {
        handleWidget(true, platformSelected);
      }
      if (resoulPlatform) {
        setDataChanged(false);
        // setLoadingState(false);
        presentSuccessModal({
          message: 'Se han guardado los cambios',
          title: '',
          modalSize: true,
          txtButton: 'Continuar',
          padding: '20px',
          onDismiss: () => {
            history.push(ROUTES_PATH.PLUGIN);
          },
        });
      }
    } catch (error: any) {
      // setLoadingState(false);
      setDataChanged(true);
      presentSuccessModal({
        message: 'Ha ocurrido un error al guardar los cambios',
        title: '',
        srcImage: ErrorModal,
        modalSize: true,
        txtButton: 'Volver',
        padding: '20px',
        onDismiss: () => {
          history.push(ROUTES_PATH.OPERATION_DASHBOARD);
        },
      });
    }
  };

  const updateValue = (e: any) => {
    const { name, value } = e.target;
    setEcommmerceFormData((prevState: any) => ({ ...prevState, [name]: value }));
    setDataChanged(true);
  };

  const handleMacrodataEndpoint = (platform: string) => {
    switch (platform) {
      case PluginPlatforms.WOO_30:
        return '/?wc-api=WC_Gateway_Pepper&get-data=1';
      case PluginPlatforms.PSH_16:
      case PluginPlatforms.PSH_17:
        return '/?fc=module&module=pepper&controller=datatransfer';
      case PluginPlatforms.MAG_1:
      case PluginPlatforms.MAG_2:
        return '/pepper/checkout/data';
      default:
        return '';
    }
  };

  const handleCartEndpoint = (platform: string) => {
    switch (platform) {
      case PluginPlatforms.WOO_30:
        return '/?wc-api=WC_Gateway_Pepper&get-cart=1';
      case PluginPlatforms.PSH_16:
      case PluginPlatforms.PSH_17:
        return '/?fc=module&module=pepper&controller=cartdata';
      case PluginPlatforms.MAG_1:
      case PluginPlatforms.MAG_2:
        return '/pepper/checkout/cartdata';
      default:
        return '';
    }
  };

  const buildForm = () => {
    if (platformData) {
      const form = {
        url_production: platformData.url_production?.join(', ') || '',
        url_test: platformData.url_test?.join(', ') || '',
        macrodata_uri: `${platformData.macrodata_uri}${handleMacrodataEndpoint(platformData.platform)}`,
        cart_uri: `${platformData.macrodata_uri}${handleCartEndpoint(platformData.platform)}`,
      };
      setEcommmerceFormData(form);
    }
  };

  useEffect(() => {
    if (platformData) {
      setPlatformSelected(platformData.platform ?? '');
      buildForm();
    }
  }, [platformData]);

  return (
    <div className={stylesEcommerce.ecommerceDataWrapper}>
      <div className={classNames(styles.pluginHeaderWrapper, styles.ecommerceHeader)} id="ecommerce-header">
        <div className={styles.pluginTitleWrapper}>
          <IonText className={styles.titleText}>Datos del ecommerce</IonText>
        </div>
        <IonButton className={styles.publishBtn} onClick={submitEcommerceData} disabled={!dataChanged}>Publicar</IonButton>
      </div>
      <hr className={styles.separationLine} />
      <IonGrid>
        <div className={stylesEcommerce.ecommercePlatformSection}>
          <IonLabel className={classNames(styles.labelWidgetTextPlatform)}>
            Plataforma
          </IonLabel>
          <IonRow>
            <IonSelect
              className={classNames(
                styles.pluginInput,
                stylesEcommerce.pluginInputWrapper,
                styles.pluginSelect,
                {
                  'ion-invalid ion-touched': errorPlatform, // Se aplica si hay un error
                  'ion-valid': errorPlatform === null, // Se aplica si no hay error
                },
              )}
              labelPlacement="stacked"
              interface="popover"
              value={platformSelected}
              onIonChange={(e: any) => {
                const selectedPlatform = e.detail.value;
                setPlatformSelected(selectedPlatform);
                setDataChanged(true);
                validatePlatform(selectedPlatform); // Validar la plataforma seleccionada
              }}
              onIonBlur={() => validatePlatform(platformSelected)} // Validar al perder el foco
            >
              {pluginPlatform.map((platform: PluginPlatform) => (
                <IonSelectOption key={platform.platformValue} value={platform.platformValue}>
                  {platform.platformName}
                </IonSelectOption>
              ))}
            </IonSelect>
            {errorPlatform && (
            <span className={classNames(stylesEcommerce.validationError)}>
              {errorPlatform}
            </span>
            )}
          </IonRow>
        </div>
        <IonRow>
          <IonCol className={classNames(styles.colStyleData)}>
            {/* Dominio/s de producción */}
            <IonLabel className={classNames(styles.labelWidgetText)}>
              Dominio/s de producción (separados por comas)
            </IonLabel>
            <IonInput
              name="url_production"
              placeholder="URL Producción"
              className={classNames(
                styles.pluginInput,
                stylesEcommerce.pluginInputWrapper,
                {
                  'ion-invalid ion-touched': errorUrlPro, // Se aplica si hay un error
                  'ion-valid': errorUrlPro === null, // Se aplica si no hay error
                },
              )}
              value={ecommmerceFormData?.url_production}
              onIonInput={(e: any) => {
                updateValue(e);
                validateUrlPro(e.target.value);
              }}
              onIonBlur={(e: any) => validateUrlPro(e.target.value)}
            />
            {errorUrlPro && <span className={classNames(stylesEcommerce.validationError)}>{errorUrlPro}</span>}

            {/* Dominio/s de test */}
            <IonLabel className={classNames(styles.labelWidgetText)}>
              Dominio/s de test (separados por comas)
            </IonLabel>
            <IonInput
              name="url_test"
              placeholder="URL de test"
              className={classNames(
                styles.pluginInput,
                stylesEcommerce.pluginInputWrapper,
                {
                  'ion-invalid ion-touched': errorUrlTest, // Se aplica si hay un error
                  'ion-valid': errorUrlTest === null, // Se aplica si no hay error
                },
              )}
              value={ecommmerceFormData?.url_test}
              onIonInput={(e: any) => {
                updateValue(e);
                validateUrlTest(e.target.value);
              }}
              onIonBlur={(e: any) => validateUrlTest(e.target.value)}
            />
            {errorUrlTest && <span className={classNames(stylesEcommerce.validationError)}>{errorUrlTest}</span>}

            <IonLabel className={classNames(styles.labelWidgetText)}>
              Url para end point de Macrodata
            </IonLabel>
            <IonInput
              name="macrodata_uri"
              placeholder="Endpoint de Macrodata** (No modificar para plataformas estándar)"
              className={classNames(styles.pluginInput, styles.pluginInputWrapper)}
              value={ecommmerceFormData?.macrodata_uri}
              onIonInput={(e: any) => {
                updateValue(e);
              }}
            />
            <IonLabel className={classNames(styles.labelWidgetText)}>
              Url para end point de recuperación de carrito
            </IonLabel>
            <IonInput
              name="cart_uri"
              placeholder="Endpoint Carrito"
              className={classNames(styles.pluginInput, styles.pluginInputWrapper)}
              value={ecommmerceFormData?.cart_uri}
              onIonInput={(e: any) => {
                updateValue(e);
              }}
            />
          </IonCol>
        </IonRow>
      </IonGrid>
    </div>
  );
}
